import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import SEO, { breadcrumbSchema, articleSchema, faqSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setError("Article not found"));
    api.get("/blog?section=blog").then(r => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [slug]);

  if (error) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-detail-not-found">
      <p className="text-slate-500">{error}</p>
      <Link to="/blog" className="text-[#1FA59A] font-semibold mt-4 inline-block">← Back to Blog</Link>
    </div>;
  }

  if (!post) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${slug}` },
  ];

  const otherPosts = related.filter(r => r.slug !== slug).slice(0, 3);

  // Normalize the FAQ payload — AutoSEO sends `[{question, answer}]`, our
  // faqSchema() helper expects `{q, a}`. Support both shapes.
  const postFaqs = Array.isArray(post.faq_schema)
    ? post.faq_schema
        .map((f) => ({ q: f.q || f.question, a: f.a || f.answer }))
        .filter((f) => f.q && f.a)
    : [];

  const schemas = [
    breadcrumbSchema(crumbs),
    articleSchema({
      headline: post.title,
      description: post.excerpt,
      slug,
      image: post.cover_image,
      datePublished: post.created_at,
      dateModified: post.updated_at,
      basePath: "/blog",
    }),
  ];
  if (postFaqs.length) schemas.push(faqSchema(postFaqs));

  return (
    <div data-testid={`page-blog-detail-${slug}`}>
      <SEO
        title={`${post.title} | OEC Blog`}
        description={post.excerpt}
        path={`/blog/${slug}`}
        image={post.cover_image}
        schema={schemas}
      />
      <section className="bg-[#0A0F1C] text-white pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <Link to="/blog" className="text-white/50 hover:text-white text-sm inline-flex items-center gap-2 mb-10" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-5">{post.category}</p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight leading-[1.1]">{post.title}</h1>
          <p className="mt-8 text-lg text-white/70 leading-relaxed">{post.excerpt}</p>
          <p className="mt-6 text-xs text-white/40 uppercase tracking-[0.2em]">
            Published {new Date(post.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </section>

      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 -mt-16 lg:-mt-24" data-testid="blog-detail-cover-wrap">
          <img
            src={post.cover_image}
            alt=""
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full aspect-[21/9] object-cover border border-slate-200"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="prose prose-slate prose-lg max-w-none
          prose-headings:font-display prose-headings:tracking-tight
          prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
          prose-p:text-slate-600 prose-p:leading-relaxed
          prose-ul:text-slate-600 prose-li:my-1
          prose-strong:text-slate-900
          prose-a:text-[#1FA59A] prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      {postFaqs.length > 0 && (
        <section className="bg-[#F8FAFC] border-t border-slate-200 py-16 lg:py-20" data-testid="blog-faq-section">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Frequently asked</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-10">Questions on this topic.</h2>
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {postFaqs.map((f, i) => (
                <details key={i} className="group py-5" data-testid={`blog-faq-${i}`}>
                  <summary className="flex items-start gap-4 cursor-pointer list-none">
                    <span className="font-display text-lg font-semibold text-slate-900 flex-1">{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none select-none">+</span>
                  </summary>
                  <div className="mt-3 text-slate-700 leading-[1.75] text-base" dangerouslySetInnerHTML={{ __html: f.a }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {otherPosts.length > 0 && (
        <section className="bg-[#F8FAFC] py-16 border-t border-slate-200">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-8">More from the blog</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherPosts.map((rp) => (
                <Link key={rp.id} to={`/blog/${rp.slug}`} className="bg-white border border-slate-200 p-6 hover:border-[#1FA59A] transition-colors" data-testid={`related-post-${rp.slug}`}>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">{rp.category}</p>
                  <h3 className="font-display text-lg font-semibold text-slate-900 leading-snug">{rp.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-16 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Need this kind of expertise on your portfolio?</h2>
          <Link to="/contact" className="gs-gradient text-white font-bold px-8 py-4 mt-6 inline-block hover:opacity-90" data-testid="blog-detail-cta-contact">
            Speak to a consultant
          </Link>
        </div>
      </section>
    </div>
  );
}
